'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  Trophy, 
  TrendingUp,
  Users,
  Target,
  ArrowRight,
  Building,
  Calendar,
  BarChart3,
  CheckCircle,
  Star,
  Quote,
  Zap,
  Globe,
  Award,
  MessageSquare,
  DollarSign,
  Clock,
  Network
} from 'lucide-react'
import Image from 'next/image'

const caseStudies = [
  {
    id: 1,
    company: "TechCorp Global",
    industry: "Technology",
    logo: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&h=100&auto=format&fit=crop&q=60",
    title: "300% Increase in Meaningful Connections at Annual Tech Conference",
    summary: "How TechCorp transformed their 2,000-person conference from awkward small talk to genuine business partnerships.",
    challenge: "TechCorp's annual conference attracted top talent but suffered from poor networking outcomes. Attendees complained about superficial conversations and missed opportunities to connect with relevant professionals.",
    solution: "Implemented SocialLoop's AI matching system with voice-activated networking games and real-time analytics to optimize connection quality.",
    results: [
      { metric: "Connection Quality", value: "+300%", description: "Meaningful conversations that led to follow-ups" },
      { metric: "Attendee Satisfaction", value: "9.2/10", description: "Average rating for networking experience" },
      { metric: "Business Partnerships", value: "45", description: "New partnerships formed during the event" },
      { metric: "Follow-up Rate", value: "87%", description: "Attendees who connected post-event" }
    ],
    testimonial: {
      quote: "SocialLoop completely revolutionized our conference. Instead of random conversations, our attendees formed genuine business relationships. The ROI on our event investment increased dramatically.",
      author: "Sarah Chen",
      title: "VP of Events",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b3b4?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2",
    featured: true
  },
  {
    id: 2,
    company: "StartupHub",
    industry: "Venture Capital",
    logo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&auto=format&fit=crop&q=60",
    title: "Startup Pitch Night: 67% More Investor-Founder Matches",
    summary: "How StartupHub used AI matching to connect the right investors with promising startups at their monthly networking event.",
    challenge: "Monthly pitch nights were chaotic with 200+ attendees. Founders struggled to find relevant investors, and VCs missed promising opportunities due to poor event flow.",
    solution: "Deployed SocialLoop's smart matching algorithm to pre-match founders with investors based on industry focus, stage preferences, and investment thesis alignment.",
    results: [
      { metric: "Investor Matches", value: "+67%", description: "Relevant investor-founder connections" },
      { metric: "Funding Secured", value: "$12M", description: "Total funding raised from connections made" },
      { metric: "Time to Match", value: "3 min", description: "Average time to find relevant connections" },
      { metric: "Event Efficiency", value: "+45%", description: "More productive conversations per hour" }
    ],
    testimonial: {
      quote: "The AI matching was incredible. Instead of founders pitching to random investors, they were connected with VCs who were genuinely interested in their space. We've never seen this level of efficiency.",
      author: "Marcus Rodriguez",
      title: "Managing Partner",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#70b5ff]",
    rotateClass: "-rotate-1",
    featured: true
  },
  {
    id: 3,
    company: "DevConnect",
    industry: "Software Development",
    logo: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=100&h=100&auto=format&fit=crop&q=60",
    title: "Voice-Powered Networking for Accessibility-First Meetup",
    summary: "Creating an inclusive networking environment for developers with diverse accessibility needs through voice-first interaction design.",
    challenge: "Traditional networking events excluded developers with mobility or visual impairments. DevConnect wanted to create truly accessible professional networking experiences.",
    solution: "Leveraged SocialLoop's voice-activated features to enable hands-free networking, conversation starters, and profile sharing for all attendees regardless of physical abilities.",
    results: [
      { metric: "Accessibility Score", value: "98%", description: "Attendees who found the event fully accessible" },
      { metric: "Participation Rate", value: "+89%", description: "Increase in active networking participation" },
      { metric: "Developer Connections", value: "340", description: "New professional connections formed" },
      { metric: "Collaboration Projects", value: "23", description: "Open source projects started from connections" }
    ],
    testimonial: {
      quote: "For the first time, I could network as effectively as anyone else. The voice interface removed all barriers and let me focus on making genuine connections with fellow developers.",
      author: "Alex Kim",
      title: "Senior Developer",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#79d45e]",
    rotateClass: "rotate-3",
    featured: false
  },
  {
    id: 4,
    company: "WomenInTech Summit",
    industry: "Professional Development",
    logo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&auto=format&fit=crop&q=60",
    title: "Breaking Barriers: 95% of Attendees Made Career-Advancing Connections",
    summary: "Empowering women in technology to build stronger professional networks and advance their careers through strategic relationship building.",
    challenge: "Women in tech often struggle with networking in male-dominated environments. The summit needed to create a supportive space for meaningful career development connections.",
    solution: "Used SocialLoop's conversation starters and mentorship matching features to facilitate connections between junior developers and senior leaders, plus peer networking opportunities.",
    results: [
      { metric: "Career Advancement", value: "95%", description: "Attendees who made career-advancing connections" },
      { metric: "Mentorship Matches", value: "78", description: "Successful mentor-mentee pairs formed" },
      { metric: "Job Opportunities", value: "156", description: "Job referrals and opportunities shared" },
      { metric: "Confidence Boost", value: "92%", description: "Attendees who felt more confident networking" }
    ],
    testimonial: {
      quote: "I connected with my current mentor and received two job referrals, all because SocialLoop helped me find the right people to talk to. It completely changed my career trajectory.",
      author: "Diana Park",
      title: "Software Engineer",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#f4889a]",
    rotateClass: "-rotate-2",
    featured: false
  },
  {
    id: 5,
    company: "Global Innovation Summit",
    industry: "Innovation & R&D",
    logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&auto=format&fit=crop&q=60",
    title: "International Networking: 40+ Countries, Zero Language Barriers",
    summary: "Connecting innovators across linguistic and cultural boundaries at the world's largest innovation conference.",
    challenge: "5,000 attendees from 40+ countries struggled with language barriers and cultural differences that hindered meaningful professional connections.",
    solution: "Implemented SocialLoop's multi-language support and cultural awareness features to enable seamless cross-cultural networking and collaboration.",
    results: [
      { metric: "Cross-Cultural Connections", value: "2,400", description: "International partnerships formed" },
      { metric: "Language Barriers", value: "-85%", description: "Reduction in communication difficulties" },
      { metric: "Innovation Projects", value: "67", description: "New international collaborations started" },
      { metric: "Global Partnerships", value: "34", description: "Formal business partnerships established" }
    ],
    testimonial: {
      quote: "Despite speaking different languages, I connected with innovators from 12 countries. The platform broke down every barrier and helped us focus on our shared passion for innovation.",
      author: "Hiroshi Tanaka",
      title: "R&D Director",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#ba81ee]",
    rotateClass: "rotate-1",
    featured: false
  },
  {
    id: 6,
    company: "Healthcare Innovation Forum",
    industry: "Healthcare",
    logo: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=100&h=100&auto=format&fit=crop&q=60",
    title: "Saving Lives Through Better Medical Professional Networks",
    summary: "How improved networking among healthcare professionals led to breakthrough medical collaborations and patient outcomes.",
    challenge: "Medical professionals at the forum needed to connect across specialties to address complex healthcare challenges, but traditional networking wasn't facilitating the right interdisciplinary connections.",
    solution: "Used SocialLoop's expertise-based matching to connect doctors, researchers, and healthcare innovators based on complementary specializations and research interests.",
    results: [
      { metric: "Medical Collaborations", value: "89", description: "New interdisciplinary research projects" },
      { metric: "Research Papers", value: "23", description: "Co-authored publications from connections" },
      { metric: "Patient Impact", value: "10,000+", description: "Patients benefiting from new collaborations" },
      { metric: "Breakthrough Solutions", value: "7", description: "Innovative treatments developed" }
    ],
    testimonial: {
      quote: "I connected with a cardiologist whose research perfectly complemented my neurology work. Together, we've developed a treatment that's helping thousands of patients. SocialLoop made that connection possible.",
      author: "Dr. Emily Watson",
      title: "Chief of Neurology",
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=80&h=80&auto=format&fit=crop&q=60"
    },
    bgColor: "bg-[#4928fd]",
    rotateClass: "-rotate-3",
    featured: true
  }
]

const industries = [
  "All Industries",
  "Technology",
  "Venture Capital", 
  "Software Development",
  "Professional Development",
  "Innovation & R&D",
  "Healthcare"
]

const metrics = [
  {
    icon: TrendingUp,
    title: "Average ROI Increase",
    value: "340%",
    description: "Across all events using SocialLoop",
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2"
  },
  {
    icon: Users,
    title: "Successful Connections",
    value: "250K+",
    description: "Meaningful professional relationships formed",
    bgColor: "bg-[#70b5ff]",
    rotateClass: "-rotate-1"
  },
  {
    icon: Building,
    title: "Enterprise Clients",
    value: "500+",
    description: "Organizations trust SocialLoop for networking",
    bgColor: "bg-[#79d45e]",
    rotateClass: "rotate-3"
  },
  {
    icon: Target,
    title: "Connection Success Rate",
    value: "94%",
    description: "Attendees who made valuable connections",
    bgColor: "bg-[#f4889a]",
    rotateClass: "-rotate-2"
  }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function CaseStudiesPage() {
  const [selectedIndustry, setSelectedIndustry] = useState("All Industries")
  const [featuredOnly, setFeaturedOnly] = useState(false)

  const filteredCaseStudies = caseStudies.filter(study => {
    const matchesIndustry = selectedIndustry === "All Industries" || study.industry === selectedIndustry
    const matchesFeatured = !featuredOnly || study.featured
    return matchesIndustry && matchesFeatured
  })

  return (
    <div className="w-full" style={{ backgroundColor: '#fdf1d3' }}>
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#4928fd] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#ba81ee] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#79d45e] border-4 border-black transform -rotate-12 shadow-xl"></div>

        {/* Floating sticky note */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"Real results from real events!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <Trophy className="mr-2 h-4 w-4" />
                  SUCCESS STORIES
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#ffaf68] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                REAL RESULTS<br />FROM REAL EVENTS
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
              <p className="text-lg font-semibold text-black">
                See how organizations across industries have transformed their networking events with SocialLoop. 
                These aren't just numbers – they're stories of real people making meaningful professional connections.
              </p>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#79d45e] border-4 border-black p-3 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                  <Badge variant="outline" className="border-black text-black bg-transparent">
                    <BarChart3 className="mr-2 h-4 w-4" />
                    PROVEN IMPACT
                  </Badge>
                </div>
              </AnimatedGroup>
              
              <div className="bg-[#70b5ff] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                <h2 className="text-3xl md:text-4xl font-black text-black">
                  THE NUMBERS SPEAK FOR THEMSELVES
                </h2>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {metrics.map((metric, index) => (
                <AnimatedGroup
                  key={metric.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`${metric.bgColor} border-4 border-black p-6 transform hover:rotate-0 transition-all duration-200 shadow-xl ${metric.rotateClass} h-full text-center`}>
                    <div className="mx-auto mb-4 w-12 h-12 bg-white border-4 border-black rounded-full flex items-center justify-center">
                      <metric.icon className="h-6 w-6 text-black" />
                    </div>
                    <div className="text-3xl font-black text-black mb-2">{metric.value}</div>
                    <h3 className="text-lg font-black text-black mb-2">{metric.title}</h3>
                    <p className="text-black font-semibold text-sm">{metric.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-2 -left-2 w-4 h-4 bg-white border-2 border-black transform rotate-45"></div>
                    <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-black rounded-full"></div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="relative px-6 py-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 lg:grid-cols-2 items-center">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h3 className="font-black text-black mb-3">Filter by Industry:</h3>
                  <div className="flex flex-wrap gap-2">
                    {industries.map((industry) => (
                      <button
                        key={industry}
                        onClick={() => setSelectedIndustry(industry)}
                        className={`px-3 py-1 border-2 border-black font-black text-xs transform hover:rotate-0 transition-all duration-200 ${
                          selectedIndustry === industry
                            ? 'bg-[#79d45e] rotate-0'
                            : 'bg-white -rotate-1 hover:bg-gray-50'
                        }`}>
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#ba81ee] border-4 border-black p-4 transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <button
                    onClick={() => setFeaturedOnly(!featuredOnly)}
                    className={`flex items-center gap-2 px-4 py-2 border-4 border-black font-black transform hover:rotate-0 transition-all duration-200 ${
                      featuredOnly ? 'bg-[#ffaf68] rotate-0' : 'bg-white rotate-1'
                    }`}>
                    <Star className="h-4 w-4" />
                    {featuredOnly ? 'Show All Stories' : 'Featured Stories Only'}
                  </button>
                </div>
              </AnimatedGroup>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#ba81ee' }}>
          <div className="mx-auto max-w-7xl">
            {filteredCaseStudies.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-white border-4 border-black p-8 inline-block transform rotate-1">
                  <p className="text-lg font-black text-black">No case studies found for the selected filters.</p>
                  <Button 
                    onClick={() => {
                      setSelectedIndustry("All Industries")
                      setFeaturedOnly(false)
                    }}
                    className="mt-4 bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black">
                    Clear Filters
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-16">
                {filteredCaseStudies.map((study, index) => (
                  <AnimatedGroup
                    key={study.id}
                    variants={{
                      item: {
                        ...transitionVariants,
                        visible: {
                          ...transitionVariants.visible,
                          transition: {
                            ...transitionVariants.visible.transition,
                            delay: index * 0.1
                          }
                        }
                      }
                    }}>
                    <div className={`grid gap-8 lg:grid-cols-3 ${study.bgColor} border-4 border-black p-8 transform hover:rotate-0 transition-all duration-200 shadow-xl ${study.rotateClass}`}>
                      {/* Company Info & Challenge */}
                      <div className="space-y-6">
                        <div className="flex items-center gap-4">
                          <Image
                            src={study.logo}
                            alt={study.company}
                            width={60}
                            height={60}
                            className="rounded-full border-4 border-black"
                          />
                          <div>
                            <h3 className="text-xl font-black text-black">{study.company}</h3>
                            <p className="font-semibold text-black/80">{study.industry}</p>
                            {study.featured && (
                              <div className="bg-white border-2 border-black px-2 py-1 text-xs font-black text-black inline-block mt-1">
                                FEATURED
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-black text-black mb-2">Challenge:</h4>
                          <p className="text-black font-semibold text-sm">{study.challenge}</p>
                        </div>

                        <div>
                          <h4 className="font-black text-black mb-2">Solution:</h4>
                          <p className="text-black font-semibold text-sm">{study.solution}</p>
                        </div>
                      </div>

                      {/* Results */}
                      <div>
                        <h4 className="font-black text-black mb-4">Results:</h4>
                        <div className="space-y-4">
                          {study.results.map((result, resultIndex) => (
                            <div key={resultIndex} className="bg-white border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                              <div className="text-2xl font-black text-black mb-1">{result.value}</div>
                              <div className="font-black text-black text-sm mb-1">{result.metric}</div>
                              <div className="font-semibold text-black/70 text-xs">{result.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Testimonial & CTA */}
                      <div className="space-y-6">
                        <div>
                          <h2 className="text-2xl font-black text-black mb-4 line-clamp-2">{study.title}</h2>
                          <p className="text-black font-semibold mb-6">{study.summary}</p>
                        </div>

                        <div className="bg-white border-4 border-black p-4 transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                          <Quote className="h-6 w-6 text-black mb-3" />
                          <p className="text-black font-semibold text-sm mb-4 italic">"{study.testimonial.quote}"</p>
                          <div className="flex items-center gap-3">
                            <Image
                              src={study.testimonial.avatar}
                              alt={study.testimonial.author}
                              width={40}
                              height={40}
                              className="rounded-full border-2 border-black"
                            />
                            <div>
                              <p className="font-black text-black text-sm">{study.testimonial.author}</p>
                              <p className="font-semibold text-black/70 text-xs">{study.testimonial.title}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#4928fd] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                          <Button className="w-full bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-0 font-black" asChild>
                            <Link href="/contact">
                              Get Similar Results
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>

                      {/* Decorative elements */}
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-black transform rotate-45"></div>
                      <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-black rounded-full"></div>
                    </div>
                  </AnimatedGroup>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-white">
                  Ready to Write Your Success Story?
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                <p className="text-lg font-semibold text-black">
                  Join hundreds of organizations who have transformed their networking events with SocialLoop. 
                  Your success story could be next.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-[#79d45e] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black" asChild>
                    <Link href="/signup">
                      Start Your Success Story
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                    <Link href="/contact">
                      Request Case Study
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}